import type { StageResult, ValidationIssue, BlockBundle } from "../types"

/**
 * AI Judge Validation Stage — audits Cogo JSON output against premium design and aesthetic rules.
 *
 * Rules:
 *   judge-color-role-mismatch   (warning) — raw/hardcoded color values or styling classes (like text-[#ff0000] or bg-red-500)
 *   judge-typographic-casing    (warning) — all-caps, all-lowercase, or unformatted typography strings
 *   judge-cta-verb-register     (warning) — generic, low-quality CTA verbs (like "Click here", "Submit")
 */
export function runAiJudgeStage(bundle: BlockBundle): StageResult {
  const start = Date.now()
  const issues: ValidationIssue[] = []

  const blockName = (bundle.block as any)?.name ?? "block"

  // 1. Audit UI node styles, casing, and CTA verbs
  if (bundle.ui && typeof bundle.ui === "object") {
    walkUiNodes(bundle.ui, (node, path) => {
      const type = String(node.type ?? "")
      const text = String(node.text ?? "")
      const label = String(node.label ?? "")
      const className = String(node.className ?? "")

      // --- Color-Role Mismatch Check ---
      // Scan classNames and options styling for hardcoded hex colors or plain Tailwind color utilities
      const hexColorRegex = /#([0-9a-fA-F]{3,8})\b/
      // Tailwind v3/v4 color classes (like bg-red-500, text-indigo-400, border-emerald-600, etc.)
      const rawColorRegex = /\b(bg|text|border|ring|decoration)-(red|blue|green|yellow|indigo|purple|pink|emerald|orange|amber|cyan|sky|teal|violet|fuchsia|rose|gray|zinc|neutral|stone|slate)-\d+\b/

      // M6-R2 repair (2026-08-11, docs/superpowers/reports/2026-08-10-m7-
      // attribution-loops/M6-L3-attribution.md): 11/11 sampled corpus firings
      // were data-driven CONDITIONAL Tailwind class expressions (e.g.
      // `#{status === 'Approved' ? 'bg-green-100 ...' : 'bg-red-100 ...'}`),
      // not statically hardcoded colors — CLAUDE.md names exactly this
      // pattern ("Tailwind CSS 클래스를 스타일 값으로 사용") as the project's
      // canonical mechanism, so it must not be flagged. The color token(s)
      // can sit inside a `#{...}` span anywhere in the string (a static
      // prefix + trailing expression, or the whole value as one expression),
      // and the span can itself contain nested `{}` (object-literal args
      // like `.find({id: x})`) — stripBraceExpressions is depth-aware so it
      // isn't fooled by the first inner `}`. Only the STATIC remainder is
      // checked, so a genuinely hardcoded raw color/hex (no expression at
      // all) is still flagged — this narrows scope, it does not disable
      // detection.
      //
      // The old message recommended `@ds: colors.*` / `text-@ds:colors.*` as
      // the fix. That vocabulary is fabricated: zero occurrences anywhere in
      // cogo-web/packages or cogo-prompt-engine/src (grepped for this
      // repair) — no generator was ever taught it, the exact same defect
      // class as the style-mira/style-lyra tombstones (M6-R1, design-
      // rules.ts). Removed the recommendation rather than repeat it.
      const staticClassName = stripBraceExpressions(className)

      if (hexColorRegex.test(staticClassName) || rawColorRegex.test(staticClassName)) {
        issues.push({
          stage: "ai-judge",
          level: "warning",
          file: "ui.json",
          block: blockName,
          rule: "judge-color-role-mismatch",
          message: `Raw hardcoded styling classes or hex colors detected at ${path} ("${className}"). Reference a shared design-token or theme-aware utility class instead of an ad-hoc hex/color utility.`,
          path,
        })
      }

      // Check nested styling/options objects for hex codes
      if (node.options && typeof node.options === "object") {
        const optionsStr = JSON.stringify(node.options)
        if (hexColorRegex.test(optionsStr)) {
          issues.push({
            stage: "ai-judge",
            level: "warning",
            file: "ui.json",
            block: blockName,
            rule: "judge-color-role-mismatch",
            message: `Raw hardcoded hex color codes detected in options at ${path}. Visual style should be driven dynamically by design system tokens.`,
            path,
          })
        }
      }

      // --- Typographic Casing & Verb Register Audits ---
      // Apply mostly to button/link types or nodes containing text
      const isCtaNode = type.includes("button") || type.includes("link") || type.includes("cta")
      const textContent = text || label

      if (textContent) {
        // Casing check:
        // A: ALL CAPS (excluding single characters or short abbreviations like "OK", "CTA", "ID", "USD")
        const isAllCaps = textContent === textContent.toUpperCase() && /[A-Z]{4,}/.test(textContent)
        // B: all lowercase (excluding short particles)
        const isAllLowerCase = textContent === textContent.toLowerCase() && textContent.trim().split(/\s+/).length > 1
        // C: camelCase (unformatted string leak)
        const isCamelCase = /^[a-z]+[A-Z][a-zA-Z]*$/.test(textContent)

        if (isAllCaps || isAllLowerCase || isCamelCase) {
          issues.push({
            stage: "ai-judge",
            level: "warning",
            file: "ui.json",
            block: blockName,
            rule: "judge-typographic-casing",
            message: `Typographic string at ${path} is poorly formatted ("${textContent}"). Premium aesthetics ban raw casing (ALL-CAPS, all-lowercase, or camelCase). Apply proper Title Case or Sentence Case.`,
            path,
          })
        }

        // Verb Register / CTA tone audit:
        if (isCtaNode) {
          const lowerText = textContent.toLowerCase().trim()
          // Banned slop/generic terms
          const bannedCtas = ["click", "click here", "clickhere", "go", "submit", "button"]
          const isBanned = bannedCtas.some((b) => lowerText === b || lowerText.includes(b))

          if (isBanned) {
            issues.push({
              stage: "ai-judge",
              level: "warning",
              file: "ui.json",
              block: blockName,
              rule: "judge-cta-verb-register",
              message: `Generic or low-quality CTA verb register detected at ${path} ("${textContent}"). Elevate the CTA voice by using specific, action-oriented verbs (e.g., "Get Started", "Join Community", "Send Message").`,
              path,
            })
          }
        }
      }
    })
  }

  // 2. Audit flows/actions for casing and structure
  if (bundle.flow && typeof bundle.flow === "object" && bundle.flow.flows) {
    for (const [flowName, flow] of Object.entries(bundle.flow.flows as Record<string, any>)) {
      if (flow && Array.isArray(flow.steps)) {
        flow.steps.forEach((step: any, stepIdx: number) => {
          if (step.action === "showToast") {
            const msg = String(step.params?.message ?? "")
            // Audit toast message casing
            if (msg && msg === msg.toLowerCase() && msg.split(/\s+/).length > 1) {
              issues.push({
                stage: "ai-judge",
                level: "warning",
                file: "flow.json",
                block: blockName,
                rule: "judge-typographic-casing",
                message: `Toast message in flow "${flowName}" step ${stepIdx} uses raw lowercase ("${msg}"). Use polished sentence casing.`,
                path: `flow.flows.${flowName}.steps[${stepIdx}].params.message`,
              })
            }
          }
        })
      }
    }
  }

  const hasErrors = issues.some((i) => i.level === "error")
  return {
    id: "ai-judge",
    name: "AI Critique Judge Parity Audit",
    passed: !hasErrors,
    duration: Date.now() - start,
    issues,
  }
}

function walkUiNodes(
  node: unknown,
  visit: (node: Record<string, unknown>, path: string) => void,
  path = "ui.root",
): void {
  if (node === null || node === undefined || typeof node !== "object") return

  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      walkUiNodes(node[i], visit, `${path}[${i}]`)
    }
    return
  }

  const record = node as Record<string, unknown>
  visit(record, path)

  for (const [key, value] of Object.entries(record)) {
    if (key === "children" && Array.isArray(value)) {
      walkUiNodes(value, visit, `${path}.children`)
    } else if (typeof value === "object" && value !== null && key !== "options" && key !== "events") {
      walkUiNodes(value, visit, `${path}.${key}`)
    }
  }
}

/**
 * Strip balanced `#{...}` runtime-expression spans from a string, returning
 * only the static text around/between them (M6-R2 repair — see the
 * judge-color-role-mismatch call site). Depth-aware: an expression can
 * itself contain object-literal `{}` (e.g. `#{foo.find({id: x}).bar}`), so a
 * naive `/#\{[^}]*\}/` regex would stop at the FIRST `}` — the inner
 * literal's — and leave the rest of the expression (and any color classes
 * inside it) un-stripped.
 */
function stripBraceExpressions(input: string): string {
  let out = ""
  let i = 0
  while (i < input.length) {
    if (input[i] === "#" && input[i + 1] === "{") {
      let depth = 1
      let j = i + 2
      while (j < input.length && depth > 0) {
        if (input[j] === "{") depth++
        else if (input[j] === "}") depth--
        j++
      }
      i = j
      continue
    }
    out += input[i]
    i++
  }
  return out
}
